/**
 * Terms of Service — production legal text satisfying Paddle's verification
 * requirements: user accounts, subscription billing, trial terms, acceptable
 * use, AI-generated content disclaimer, limitation of liability, termination,
 * governing law, contact information.
 */
import React from "react";
import { LegalPageLayout, LegalSection } from "./LegalPageLayout";

const SECTIONS = [
  { id: "acceptance", title: "1. Acceptance of Terms" },
  { id: "service", title: "2. The Service" },
  { id: "accounts", title: "3. User Accounts" },
  { id: "subscriptions", title: "4. Subscriptions & Billing" },
  { id: "trials", title: "5. Free Trials" },
  { id: "acceptable-use", title: "6. Acceptable Use" },
  { id: "ai-content", title: "7. AI-Generated Content" },
  { id: "ip", title: "8. Intellectual Property" },
  { id: "termination", title: "9. Termination" },
  { id: "warranty", title: "10. Disclaimers & Warranties" },
  { id: "liability", title: "11. Limitation of Liability" },
  { id: "indemnity", title: "12. Indemnification" },
  { id: "changes", title: "13. Changes to Terms" },
  { id: "law", title: "14. Governing Law" },
  { id: "contact", title: "15. Contact" },
];

export default function TermsPage() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      subtitle="The rules that govern your use of Ashra AI. Please read carefully."
      lastUpdated="May 29, 2026"
      sections={SECTIONS}
    >
      <LegalSection id="acceptance" title="1. Acceptance of Terms">
        <p>
          These Terms of Service (the “Terms”) form a binding agreement between you
          (“you”, “User”, “Subscriber”) and <strong>Ashra AI</strong> (“Ashra AI”, “we”, “us”,
          “our”), the operator of the Ashra AI platform available at
          <a href="https://ashraai.com"> ashraai.com</a> (the “Service”). By creating an
          account, subscribing to a paid plan, or accessing the Service in any way,
          you confirm that you have read, understood and agree to be bound by these
          Terms and our <a href="/privacy">Privacy Policy</a>. If you do not agree,
          do not use the Service.
        </p>
      </LegalSection>

      <LegalSection id="service" title="2. The Service">
        <p>
          Ashra AI is an AI Revenue Operating System for creators, coaches,
          consultants, agencies and digital businesses. The Service provides
          AI-driven lead capture, customer engagement, automation, payment
          processing, analytics, and other revenue tools. Specific features available
          to you depend on your active subscription plan.
        </p>
      </LegalSection>

      <LegalSection id="accounts" title="3. User Accounts">
        <p>You must register an account to use most features of the Service. You agree to:</p>
        <ul>
          <li>provide accurate, current and complete information at registration;</li>
          <li>maintain and promptly update your information;</li>
          <li>keep your password and credentials confidential;</li>
          <li>be solely responsible for activity that occurs under your account;</li>
          <li>notify us immediately of any unauthorized access or suspected breach.</li>
        </ul>
        <p>
          You must be at least 18 years of age, or the age of legal majority in
          your jurisdiction, to create an account. Accounts are personal and may not
          be transferred without our prior written consent.
        </p>
      </LegalSection>

      <LegalSection id="subscriptions" title="4. Subscriptions & Billing">
        <p>
          Paid plans are billed on a recurring basis (monthly or annually depending
          on the plan you select). Pricing for each plan is displayed on
          <a href="/#pricing"> our pricing page</a> and may be updated from time to time.
        </p>
        <h3>4.1 Payment Processor</h3>
        <p>
          All payments are processed by our payment provider, <strong>Paddle</strong>
          (Paddle.com Market Ltd, a Merchant of Record). By subscribing you also
          accept Paddle's terms and privacy policy. Paddle handles checkout, tax
          collection, invoicing and customer billing data.
        </p>
        <h3>4.2 Renewal</h3>
        <p>
          Subscriptions automatically renew at the end of each billing cycle for the
          same plan and term unless cancelled before the renewal date. By
          subscribing you authorize us (through Paddle) to charge your chosen
          payment method on each renewal.
        </p>
        <h3>4.3 Cancellation</h3>
        <p>
          You may cancel your subscription at any time from
          <code> Settings → Billing</code> or via the Paddle Customer Portal link
          included in your billing emails. Cancellation takes effect at the end of
          the current paid billing cycle; you continue to have access until that
          date.
        </p>
        <h3>4.4 Price Changes</h3>
        <p>
          We may change pricing for new sign-ups at any time. Existing subscribers
          will receive at least thirty (30) days' notice before any change applies
          to their renewal.
        </p>
        <h3>4.5 Taxes</h3>
        <p>
          Prices displayed are in US Dollars and exclude applicable sales tax, VAT
          or GST. Paddle, as Merchant of Record, calculates and remits applicable
          taxes on our behalf.
        </p>
        <h3>4.6 Refunds</h3>
        <p>
          Refunds are governed by our <a href="/refund-policy">Refund Policy</a>.
        </p>
      </LegalSection>

      <LegalSection id="trials" title="5. Free Trials">
        <p>
          We offer a <strong>15-day free trial</strong> on our IGNITE and ASCEND
          plans. You will not be charged during the trial period. Unless you cancel
          before the trial ends, your subscription will automatically convert to a
          paid subscription on day 16 and your selected payment method will be
          charged.
        </p>
        <p>
          Trial eligibility is limited to one trial per user, household and
          payment method. We may revoke trial access if abuse is detected.
        </p>
      </LegalSection>

      <LegalSection id="acceptable-use" title="6. Acceptable Use">
        <p>You agree NOT to use the Service to:</p>
        <ul>
          <li>send spam, unsolicited bulk email, or violate anti-spam laws (CAN-SPAM, GDPR, CASL);</li>
          <li>send messages to contacts who have not consented to receive them;</li>
          <li>upload, generate, or distribute content that is unlawful, defamatory, hateful, sexually explicit involving minors, or infringing third-party rights;</li>
          <li>use the Service for cryptocurrency schemes, multi-level marketing, gambling, weapons, illegal drugs, or other restricted industries;</li>
          <li>reverse-engineer, decompile, or attempt to derive the source code of any part of the Service;</li>
          <li>circumvent rate limits, plan quotas or hidden fair-use safety caps;</li>
          <li>resell, sublicense or commercially redistribute the Service without our prior written consent;</li>
          <li>use the Service to build a competing product or train competing AI models;</li>
          <li>interfere with the integrity or performance of the Service or related infrastructure;</li>
          <li>scrape, harvest or collect data from the Service through automated means.</li>
        </ul>
        <p>
          We may, at our sole discretion, suspend or terminate accounts that violate
          this section.
        </p>
      </LegalSection>

      <LegalSection id="ai-content" title="7. AI-Generated Content">
        <p>
          The Service uses third-party AI models (including but not limited to
          OpenAI, Anthropic and Google). AI-generated outputs may contain
          inaccuracies, errors, biases, or content that does not reflect current
          facts. <strong>You are solely responsible</strong> for reviewing,
          verifying, and approving any AI-generated content before publishing,
          sending or relying on it.
        </p>
        <p>
          Ashra AI makes no warranty as to the accuracy, completeness, suitability
          or non-infringement of AI-generated outputs. Outputs are provided "as is"
          and should not be considered legal, financial, medical or professional
          advice.
        </p>
        <p>
          You retain ownership of the prompts you submit and the outputs generated
          on your behalf (subject to the rights of the underlying model providers).
          We may use anonymized usage metadata to improve the Service.
        </p>
      </LegalSection>

      <LegalSection id="ip" title="8. Intellectual Property">
        <p>
          The Service, including its underlying software, design, brand, trademarks
          and documentation, is and remains the exclusive property of Ashra AI and
          its licensors. We grant you a limited, non-exclusive, non-transferable,
          revocable license to use the Service in accordance with these Terms for
          the duration of your subscription.
        </p>
        <p>
          You retain ownership of the content, data, contacts, leads and assets you
          upload or generate through the Service ("Customer Data"). You grant us a
          worldwide, non-exclusive license to host, process and transmit Customer
          Data solely for the purpose of providing the Service to you.
        </p>
      </LegalSection>

      <LegalSection id="termination" title="9. Termination">
        <p>
          You may terminate your account at any time from
          <code> Settings → Billing</code>. We may suspend or terminate your account
          immediately, with or without notice, if you:
        </p>
        <ul>
          <li>breach these Terms or our Acceptable Use policy;</li>
          <li>fail to pay subscription fees when due;</li>
          <li>engage in fraudulent or abusive conduct;</li>
          <li>place our infrastructure or other users at risk.</li>
        </ul>
        <p>
          Upon termination, your right to use the Service ends immediately. We may
          delete your Customer Data after a reasonable retention period as
          described in our <a href="/privacy">Privacy Policy</a>.
        </p>
      </LegalSection>

      <LegalSection id="warranty" title="10. Disclaimers & Warranties">
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
          ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, INCLUDING
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          NON-INFRINGEMENT, OR UNINTERRUPTED AVAILABILITY.
        </p>
        <p>
          We do not guarantee any particular result, revenue increase, lead volume,
          or business outcome from your use of the Service.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="11. Limitation of Liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL ASHRA AI, ITS
          AFFILIATES, OFFICERS, EMPLOYEES, AGENTS OR LICENSORS BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE OR EXEMPLARY
          DAMAGES, INCLUDING LOSS OF PROFITS, REVENUE, DATA, USE, GOODWILL OR OTHER
          INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF OR
          INABILITY TO USE THE SERVICE.
        </p>
        <p>
          OUR AGGREGATE LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE SHALL NOT
          EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE
          EVENT GIVING RISE TO THE CLAIM, OR ONE HUNDRED US DOLLARS (US $100),
          WHICHEVER IS GREATER.
        </p>
      </LegalSection>

      <LegalSection id="indemnity" title="12. Indemnification">
        <p>
          You agree to indemnify, defend and hold harmless Ashra AI, its affiliates,
          officers, employees, agents and licensors from any claims, damages,
          liabilities, costs and expenses (including reasonable attorneys' fees)
          arising out of (a) your use of the Service, (b) your violation of these
          Terms, (c) your violation of any third-party right, including privacy or
          intellectual property rights, or (d) any content you upload or generate
          through the Service.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="13. Changes to Terms">
        <p>
          We may modify these Terms from time to time. We will post the updated
          Terms on this page and update the "Last updated" date. For material
          changes we will provide at least thirty (30) days' notice (by email or
          in-app banner) before the changes take effect. Your continued use of the
          Service after the effective date constitutes acceptance of the updated
          Terms.
        </p>
      </LegalSection>

      <LegalSection id="law" title="14. Governing Law">
        <p>
          These Terms are governed by the laws of the State of Delaware, United
          States of America, without regard to its conflict-of-laws provisions. Any
          dispute arising out of or relating to these Terms or the Service shall be
          resolved exclusively in the state or federal courts located in Delaware,
          United States, and you consent to the personal jurisdiction of those
          courts.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="15. Contact">
        <p>
          Questions about these Terms? Contact us:
        </p>
        <ul>
          <li><strong>Email:</strong> <a href="mailto:legal@ashraai.com">legal@ashraai.com</a></li>
          <li><strong>Support:</strong> <a href="mailto:support@ashraai.com">support@ashraai.com</a></li>
          <li><strong>Website:</strong> <a href="https://ashraai.com">https://ashraai.com</a></li>
        </ul>
      </LegalSection>
    </LegalPageLayout>
  );
}
